/**
 * Modal — centered dialog with backdrop, focus trap, Escape to close.
 * Panels pass their own footer so the primary action stays explicit.
 */
import { useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useFocusTrap } from '@/lib/focus-trap';

export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  initialFocus,
}: {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children?: ReactNode;
  footer?: ReactNode;
  initialFocus?: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Per-instance ids: two modals in the same tree must not share `modal-title`.
  const id = useId();
  const titleId = `${id}-title`;
  const descId = `${id}-desc`;
  useFocusTrap(panelRef, { active: open, onEscape: onClose, initialFocus });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center p-0 sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-navy-dark/50" onClick={onClose} aria-hidden />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className="relative w-full max-w-md rounded-t-xl bg-white shadow-2xl outline-none sm:rounded-xl overscroll-contain"
      >
        <div className="flex items-start gap-3 px-5 pt-5">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-base font-semibold text-gray-900">
              {title}
            </h2>
            {description && (
              <p id={descId} className="mt-1 text-sm text-muted">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 -mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          >
            <X size={16} strokeWidth={2} aria-hidden />
          </button>
        </div>
        {children && <div className="px-5 py-4">{children}</div>}
        {footer && (
          <div className="flex flex-col-reverse gap-2 border-t border-gray-100 px-5 py-3 sm:flex-row sm:justify-end">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
